import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Package, RefreshCw } from 'lucide-react';
import { dispatchService } from '@/services/dispatch';
import type { DispatchOrder } from '@/types/dispatch';
import { fmt } from '@/lib/format';

/**
 * Pack picker — full-bleed list of PENDING dispatches. iOS-style
 * large title; cards stretch edge-to-edge; skeleton placeholders
 * during fetch.
 */
export function PwaPackPickerPage() {
  const [rows, setRows] = useState<DispatchOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    dispatchService
      .list({ status: 'PENDING', pageSize: 50 })
      .then((res) => {
        if (!cancelled) setRows(res.rows);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
          setRows([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return (
    <div className="pwa-anim-in flex flex-col gap-3 px-4 pb-6 pt-3">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
            Pack
          </p>
          <h1 className="text-[26px] font-bold leading-tight tracking-tight text-foreground">
            Awaiting pack
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          aria-label="Refresh"
          className="pwa-tap inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted/60 text-foreground active:bg-muted"
        >
          <RefreshCw className={`h-4 w-4 ${rows === null ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {rows === null && !error && <PickerSkeleton />}

      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-[13px] text-destructive">
          {error}
        </div>
      )}

      {rows && rows.length === 0 && !error && (
        <EmptyState
          icon={<Package className="h-10 w-10" />}
          title="No dispatches waiting"
          subtitle="Pending dispatches will appear here as they're created."
        />
      )}

      {rows && rows.length > 0 && (
        <ul className="space-y-2">
          {rows.map((d) => (
            <li key={d.id}>
              <Link
                to={`/pwa/pack/${d.id}`}
                className="pwa-tap flex items-center gap-3 rounded-2xl border border-border/70 bg-card px-4 py-3.5 shadow-elev-1 active:bg-muted/40"
              >
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                  <Package className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[14px] font-semibold leading-tight text-foreground">
                    {d.dispatchNumber}
                  </div>
                  <div className="mt-0.5 truncate text-[12px] text-muted-foreground">
                    {d.salesOrder?.customer?.name ?? '—'}
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground/80">
                    Order {d.salesOrder?.orderNumber ?? '—'} · {fmt.relative(d.createdAt)}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-foreground/30" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PickerSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2, 3, 4].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card px-4 py-3.5"
        >
          <div className="h-11 w-11 shrink-0 rounded-xl bg-muted animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-32 rounded bg-muted animate-pulse" />
            <div className="h-3 w-40 rounded bg-muted/70 animate-pulse" />
            <div className="h-2.5 w-24 rounded bg-muted/50 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/70 bg-card px-6 py-12 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-2xl bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="space-y-1">
        <div className="text-[14px] font-semibold text-foreground">{title}</div>
        <p className="text-[12px] text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
